<template>
  <v-row
    no-gutters
    class="pr-2"
    :class="{
      'pt-5': computedHasTitleRow,
      'fill-height': !loaded || !mapPanelDataFetched,
      'align-center': !loaded || !mapPanelDataFetched,
    }"
  >
    <v-col v-if="loaded && mapPanelDataFetched" cols="12" class="py-0 px-0">
      <card-title-row
        v-if="computedHasTitleRow"
        :has-back-button="hasBackButton && !isNew"
        :straight-title="computedCardTitle"
        :has-tools-buttons="true"
        @action="backButtonAction"
      >
        <template #buttons>
          <dots-menu
            v-if="usesPermissions ? hasPermission && !isNew : true"
            :disabled="isEditing"
            :items="objectDotsMenuItems"
            @deleteObject="showDeleteObjectDialog"
            @editObject="showEditObject"
          ></dots-menu>
        </template>
        <template #toolsButtons>
          <v-row class="pt-4 pb-4">
            <base-rounded-button
              v-if="hasViewPermission && !isNew && hasGeometry"
              :tooltip="$i18n.t(`sidebar.zoomOnMap`)"
              color="rgba(235, 235, 235, 0.5)"
              iconColor="#202124"
              icon="mdi-magnify"
              size="x-small"
              @click="zoomToObject(moduleLayerId, 'layers', currentObjectId)"
            />
            <base-rounded-button
              v-if="hasPermission && !isNew && hasNewObjects && hasAdd"
              color="rgba(235, 235, 235, 0.5)"
              iconColor="disabled"
              icon="mdi-plus"
              size="x-small"
              :tooltip="$i18n.t(`sidebar.${moduleName}Module.addOne`)"
              @click="toggleObjectAdding"
            />
            <base-rounded-button
              class="pt-5"
              v-if="isUrlButtonVisible && !isNew"
              :tooltip="$i18n.t(`sidebar.${moduleName}Module.urlButton`)"
              color="rgba(235, 235, 235, 0.5)"
              iconColor="#202124"
              icon="$wifi"
              icon-size="16px"
              size="x-small"
              @click="goToUrl"
            />
            <feature-card-show-buttons
              v-if="!isNew"
              :permissions="modulePermissions.attachments_and_comments_value"
              @showNotes="showExtrasTab('notes')"
              @showHistory="showExtrasTab('history')"
              @showAttachments="showExtrasTab('attachments')"
              @showRelations="showExtrasTab('relations')"
              :featureProps="payload"
              :icons-to-show="iconsToShow"
            ></feature-card-show-buttons>
            <feature-card-pdf-button
              v-if="printLayer"
              :featureProperties="payload"
              :feature="currentObject"
              :layer="printLayer"
              :formSchema="schema"
              :file-name="computedCardTitle"
            />
          </v-row>
        </template>
      </card-title-row>
      <slot name="extraContent" v-bind="payload"> </slot>
      <sidebar-form
        v-model="payload"
        :is-valid.sync="isSidebarFormValid"
        :inputs="getInputs"
        :is-editing-on="isEditing"
      >
        <template v-for="(value, key) in $scopedSlots" #[key]="scope">
          <slot
            :name="key"
            v-bind="{
              ...scope,
              ...{ payload: $data.payload, objects: $data.objects, slotsValid: $data.slotsValid },
            }"
          />
        </template>
      </sidebar-form>
      <v-form v-model="isFormValid">
        <feature-card-data
          ref="form"
          :elements="schema"
          v-model="payload"
          :is-editing-on="isEditing"
          :layer-id="moduleLayerId"
          arrows-mode="on-with-storage"
          @change="payload = $event"
        >
        </feature-card-data>
      </v-form>

      <object-extras
        ref="extras"
        v-if="(currentObjectId || currentObjectId === 0) && !isNew"
        :dataSourceName="moduleDataSourceName"
        :featureId="currentObjectId"
        :layerId="moduleLayerId"
        persist-old-extras
        :permissions="modulePermissions.attachments_and_comments_value"
        :hide-divider="schema.length < 1"
        :tabs="customObjectExtrasTabs"
        :geometry="currentObjectGeometry"
      />
    </v-col>
    <v-col v-else>
      <main-loader></main-loader>
    </v-col>
    <main-dialog
      v-if="hasDeletingObjects && isDeleteDialogLoaded"
      :is-visible.sync="isDeleteObjectDialogVisible"
      :title="$i18n.t(`sidebar.${this.moduleName}Module.deleteObject`)"
      :body="$i18n.t(`sidebar.${moduleName}Module.confirmDeleteObject`, { name: currentObjectName })"
      agree-text="dialog.agreeDelete"
      :loading="isDeleteLoading"
      @agree="objectDeleting"
    ></main-dialog>
  </v-row>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DotsMenu from '@/components/DotsMenu';
import FeatureCardData from '@/components/FeatureCardData';
import MainLoader from '@/components/MainLoader';
import ObjectExtras from '@/components/ObjectExtras';
import SidebarForm from '@/components/SidebarForm';
import FeatureCardShowButtons from '@/components/FeatureCardShowButtons.vue';
import FeatureCardPdfButton from '@/components/FeatureCardPdfButton';

import defaultValues from '@/mixins/defaultValues';
import featureCardZdmButtonsMixin from '@/mixins/featureCardZdmButtonsMixin';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'SidebarModuleInfo',
  mixins: [defaultValues, featureCardZdmButtonsMixin, prepareAttributesFeatureCardMixin],
  components: {
    CardTitleRow,
    DotsMenu,
    FeatureCardData,
    MainDialog: () => import('@/components/MainDialog'),
    MainLoader,
    ObjectExtras,
    SidebarForm,
    FeatureCardShowButtons,
    FeatureCardPdfButton,
  },
  props: {
    hasBasicAttributes: { type: Boolean, default: false },
    hasAdd: { type: Boolean, default: true },
    usesPermissions: { type: Boolean, default: true },
    filteredAttributes: {
      type: Array,
      default: () => [],
    },
    customFormSchema: {
      type: Object,
      default: () => {},
    },
    nameAttribute: {
      type: String,
      default: 'name',
    },
    stickyCancelTranslation: {
      type: String,
      default: 'dialog.disagree',
    },
    assignedObjects: {
      type: Object,
      default: () => {},
    },
    customCardTitle: {
      type: Object,
    },
    customCurrentObjectId: {
      type: [String, Number],
    },
    customObjectExtrasTabs: {
      type: Array,
    },
    customParentRoute: {
      type: Function,
    },
    getAssignedObjects: {
      type: Function,
      default: () => {},
    },
    getObject: {
      type: Function,
    },
    handleObjectAdding: {
      type: Function,
      default: () => {},
    },
    handleObjectDeleting: {
      type: Function,
      default: () => {},
    },
    handleObjectEditing: {
      type: Function,
      default: () => {},
    },
    hasAssignedObjects: {
      type: Boolean,
      default: true,
    },
    hasBackButton: {
      type: Boolean,
      default: false,
    },
    hasDeletingObjects: {
      type: Boolean,
      default: true,
    },
    hasGeometry: {
      type: Boolean,
      default: true,
    },
    hasLayer: {
      type: Boolean,
      default: true,
    },
    hasNewObjects: {
      type: Boolean,
      default: true,
    },
    hasTitleRow: {
      type: Boolean,
      default: true,
    },
    hasUrlButton: {
      type: Boolean,
      default: false,
    },
    iconsToShow: {
      type: Array,
      default: () => ['showHistory', 'showNotes', 'showAttachments', 'showRelations'],
    },
    initGets: {
      type: Function,
      default: () => {},
    },
    inputs: {
      type: Object,
      default: () => ({ new: [], edit: [] }),
    },
    isModal: {
      type: Boolean,
      default: false,
    },
    moduleLinks: {
      type: Object,
      default: () => {},
    },
    moduleMappingDataSourceName: {
      type: String,
      default: 'datasource_name',
    },
    moduleMappingLayerName: {
      type: String,
      default: 'layer_id',
    },
    propertyTittleAppendix: {
      type: String,
      default: '',
    },
    moduleName: {
      type: String,
      required: true,
    },
    parentModuleName: {
      type: String,
      required: false,
    },
    printLayer: {
      type: Object,
    },
    moduleObjects: {
      type: Object,
      default: () => {},
    },
    newObjectPayload: {
      type: Object,
      default: () => {},
    },
    permissionsPath: {
      type: String,
    },
    toggleObjectAdding: {
      type: Function,
      default: () => {},
    },
    zoomToObject: {
      type: Function,
      default: () => {},
    },
  },
  data: () => ({
    currentObject: undefined,
    isEditing: false,
    isDeleteObjectDialogVisible: false,
    isDeleteDialogLoaded: false,
    isDeleteLoading: false,
    isFormValid: false,
    isSidebarFormValid: false,
    isBasicValid: false,
    loaded: false,
    schema: [],
    objects: [],
    payload: {},
    slotsValid: {},
  }),
  computed: {
    mapPanelDataFetched: sync('map/mapPanelDataFetched'),
    moduleMapping: get('admin/modulesMapping'),
    moduleDataSourceName: get('admin/modulesMapping@:moduleName@:moduleMappingDataSourceName'),
    moduleLayerId: get('admin/modulesMapping@:moduleName@:moduleMappingLayerName'),
    permissions: get('users/toolsPermissions'),

    isStickyButtonsSidebarLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsSidebarValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsSidebarVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtonsSidebar: sync('sidebar/stickyButtons'),

    isStickyButtonsModalLoading: sync('sidebar/isStickyButtonsModalLoading'),
    isStickyButtonsModalValid: sync('sidebar/isStickyButtonsModalValid'),
    isStickyButtonsModalVisible: sync('sidebar/isStickyButtonsModalVisible'),
    stickyButtonsModal: sync('sidebar/stickyButtonsModal'),

    isNewCard: sync('map/isNewCard'),

    isStickyButtonsLoading: {
      get() {
        return this[`isStickyButtons${this.isModal ? 'Modal' : 'Sidebar'}Loading`];
      },
      set(nV) {
        this[`isStickyButtons${this.isModal ? 'Modal' : 'Sidebar'}Loading`] = nV;
      },
    },
    isStickyButtonsValid: {
      get() {
        return this[`isStickyButtons${this.isModal ? 'Modal' : 'Sidebar'}Valid`];
      },
      set(nV) {
        this[`isStickyButtons${this.isModal ? 'Modal' : 'Sidebar'}Valid`] = nV;
      },
    },
    isStickyButtonsVisible: {
      get() {
        return this[`isStickyButtons${this.isModal ? 'Modal' : 'Sidebar'}Visible`];
      },
      set(nV) {
        this[`isStickyButtons${this.isModal ? 'Modal' : 'Sidebar'}Visible`] = nV;
      },
    },
    stickyButtons: {
      get() {
        return this[`stickyButtons${this.isModal ? 'Modal' : 'Sidebar'}`];
      },
      set(nV) {
        this[`stickyButtons${this.isModal ? 'Modal' : 'Sidebar'}`] = nV;
      },
    },

    currentLayerFormSchema: get('layers/currentLayer@form_schema'),
    dataSourcesForms: get('layers/dataSourcesForms@:moduleDataSourceName'),
    attributesMetadata: get('layers/metadata@:moduleDataSourceName'),
    computedAppendix() {
      return this.payload[this.propertyTittleAppendix];
    },
    computedAttributes() {
      if (this.attributesMetadata) {
        return this.attributesMetadata.attributes_schema.attributes;
      }
      return undefined;
    },

    computedIsValid() {
      return this.isFormValid && this.isSidebarFormValid && this.isSlotsValid;
    },

    computedHasTitleRow() {
      if (this.hasTitleRow) {
        if (this.isNew && this.customCardTitle && !this.customCardTitle.new) {
          return false;
        } else return !(!this.isNew && this.customCardTitle && !this.customCardTitle.edit);
      } else {
        return false;
      }
    },
    currentAssignedObjects() {
      return this.assignedObjects[this.currentObjectId];
    },
    currentObjectId() {
      return this.customCurrentObjectId || this.customCurrentObjectId === null
        ? this.customCurrentObjectId
        : this.$route.params.fid;
    },
    currentObjectName() {
      return this.currentObject?.[this.nameAttribute] || this.currentObjectId;
    },
    currentObjectGeometry() {
      return this.currentObject?.geometry || null;
    },
    computedCardTitle() {
      if (this.customCardTitle) {
        if (this.isNew && this.customCardTitle.new) {
          return this.propertyTittleAppendix
            ? this.customCardTitle.new + this.computedAppendix
            : this.customCardTitle.new;
        } else if (!this.isNew && this.customCardTitle.edit) {
          return this.propertyTittleAppendix
            ? this.customCardTitle.edit + this.computedAppendix
            : this.customCardTitle.edit;
        }
      }
      return this.isNew
        ? this.$i18n.t(`sidebar.${this.moduleName}Module.addNewOne`)
        : `${this.$i18n.t(`sidebar.${this.moduleName}Module.moduleName`)}: ${this.currentObject?.[
            `${this.nameAttribute}`
          ] || ''} [ID ${this.currentObject?.id || ''}]`;
    },
    getInputs() {
      return this.inputs[this.isNew ? 'new' : 'edit'];
    },
    hasPermission() {
      return this.modulePermissions.main_value > 1;
    },
    hasViewPermission() {
      return this.modulePermissions.main_value > 0;
    },
    isNew() {
      return this.currentObjectId === 'new';
    },
    isSlotsValid() {
      for (const slotValid of this.$_objectToArray(this.slotsValid)) {
        if (!slotValid) {
          return false;
        }
      }
      return true;
    },
    isUrlButtonVisible() {
      return this.hasUrlButton && this.moduleLinks[this.currentObjectId];
    },
    modulePermissions() {
      return this.usesPermissions ? this.permissions[this.permissionsPath] : true;
    },
    objectDotsMenuItems() {
      return [
        {
          name: 'editObject',
        },
        ...(this.hasDeletingObjects ? [{ name: 'deleteObject' }] : []),
      ];
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  methods: {
    getDataSourceForm: call('layers/getDataSourceForm'),
    getRelationValuesMapping: call('layers/getRelationValuesMapping'),

    getAddedObjects() {
      const addedObjects = [];
      for (const object of this.objects) {
        if (
          !this.currentAssignedObjects.find(o => o.layer_id === object.layer_id && o.object_id === object.object_id)
        ) {
          addedObjects.push(object);
        }
      }
      return addedObjects;
    },
    getDeletedObjects() {
      const deletedObjects = [];
      for (const object of this.currentAssignedObjects) {
        if (!this.objects.find(o => o.layer_id === object.layer_id && o.object_id === object.object_id)) {
          deletedObjects.push(object.id);
        }
      }
      return deletedObjects;
    },
    goToUrl() {
      window.open(this.moduleLinks[this.currentObjectId], '_blank');
    },
    hideEditObject() {
      this.$root.$emit('moduleIdentification-action', { isActive: true, moduleRoute: this.$route.name });
      if (this.hasLayer && this.hasGeometry) {
        this.$root.$emit('refreshMvtSource', this.moduleLayerId);
      }
      if (this.isNew) {
        if (!this.isModal) {
          if (this.customParentRoute) {
            this.customParentRoute();
          } else {
            this.$router.push({
              name: this.parentModuleName ? this.parentModuleName : this.moduleName,
            });
          }
        }
      } else {
        this.isEditing = false;
        this.objects = this.hasAssignedObjects ? JSON.parse(JSON.stringify(this.currentAssignedObjects)) : [];
        this.payload = JSON.parse(JSON.stringify(this.currentObject));
        setTimeout(() => {
          this.$root.$emit('refreshObjectGeometry');
        });
        if (!this.isModal) {
          this.isStickyButtonsVisible = false;
        } else {
          this.stickyButtons = [
            {
              action: 'hideEditObject',
              translationPath: this.stickyCancelTranslation,
              cancel: true,
            },
          ];
        }
      }
    },
    showDeleteObjectDialog() {
      this.isDeleteObjectDialogVisible = true;
      this.isDeleteDialogLoaded = true;
    },
    showEditObject() {
      this.$root.$emit('moduleIdentification-action', { isActive: false, moduleRoute: '' });
      this.isEditing = true;
      this.isStickyButtonsVisible = true;
      this.stickyButtons = [
        {
          action: 'objectEditing',
          translationPath: 'dialog.save',
        },
        {
          action: this.isModal ? '' : 'hideEditObject',
          translationPath: 'dialog.disagree',
          cancel: true,
        },
      ];
    },
    backButtonAction() {
      this.$router.push({
        name: this.parentModuleName ? this.parentModuleName : this.moduleName,
      });
    },
    refreshMapAndTable() {
      if (this.hasLayer) {
        this.$root.$emit('refreshTableData');
        if (
          (this.moduleLayerId || this.moduleLayerId === 0) &&
          this.projectLayers.find(layer => layer.id === this.moduleLayerId)
        ) {
          this.$root.$emit('updateLegendStylesCounts', { layersIds: [this.moduleLayerId] });
        }
      }
      if (this.hasLayer && this.hasGeometry) {
        this.$root.$emit('refreshMvtSource', this.moduleLayerId);
      }
    },
    getValues(elements) {
      return elements.reduce((total, element) => {
        if (element.element_type === 'group') {
          return { ...total, ...this.getValues(element.elements) };
        }
        return {
          ...total,
          [element.attribute]: this.getValue(element.attribute, element.default_value_policy),
        };
      }, {});
    },
    getValue(attribute, defaultValuePolicy) {
      return defaultValuePolicy ? this.getDefaultValueHandler(defaultValuePolicy) : null;
    },
    getFormSchema() {
      if (this.customFormSchema && Object.keys(this.customFormSchema).length > 0) {
        return this.customFormSchema;
      }
      if (this.currentLayerFormSchema && Object.keys(this.currentLayerFormSchema).length > 0) {
        return this.currentLayerFormSchema;
      }
      return this.dataSourcesForms;
    },
    filterElements(filterElements) {
      return this.$_filterNestedArray(
        filterElements,
        item => !this.filteredAttributes.includes(item.attribute),
        'elements'
      );
    },

    async init() {
      this.loaded = false;
      if (!this.mapPanelDataFetched) {
        return;
      }
      if (this.hasBasicAttributes) {
        await this.getDataSourceForm(this.moduleDataSourceName);
        this.schema = await this.prepareFormSchema();
      }
      this.isStickyButtonsLoading = false;
      this.isStickyButtonsVisible = false;
      await this.initGets();
      this.isNew ? await this.initNew() : await this.initEdit();
      this.loaded = true;
    },
    async initNew() {
      const values = this.getValues(this.schema);
      this.payload = { ...this.newObjectPayload, ...values } || {};
      this.objects = [];
      this.$root.$off('hideEditObject');
      this.$root.$off('objectAdding');
      this.$root.$on('hideEditObject', this.hideEditObject);
      this.$root.$on('objectAdding', this.objectAdding);

      this.isEditing = true;
      this.stickyButtons = [
        {
          action: 'objectAdding',
          translationPath: 'dialog.agreeAdd',
        },
        {
          action: this.isModal ? '' : 'hideEditObject',
          translationPath: this.stickyCancelTranslation,
          cancel: true,
        },
      ];
      this.isStickyButtonsValid = false;
      this.isStickyButtonsVisible = true;

      if (!this.$route.params.selectedFeatures) {
        return;
      } else {
        const featuresIds = this.$route.params.selectedFeatures.featuresIds;
        const layerId = this.$route.params.selectedFeatures.layerId;

        if (featuresIds.length > 0 && layerId) {
          const assignedObjects = [];
          for (const featureId of featuresIds) {
            assignedObjects.push({ layer_id: layerId, object_id: featureId });
          }
          this.objects = JSON.parse(JSON.stringify(assignedObjects));
        }
      }
    },
    async initEdit() {
      this.stickyButtons = [
        {
          action: this.isModal ? '' : 'hideEditObject',
          translationPath: this.stickyCancelTranslation,
          cancel: true,
        },
      ];
      this.$root.$off('hideEditObject');
      this.$root.$off('objectEditing');
      this.$root.$on('hideEditObject', this.hideEditObject);
      this.$root.$on('objectEditing', this.objectEditing);

      this.currentObject = this.getObject
        ? await this.getObject(this.currentObjectId)
        : this.moduleObjects[this.currentObjectId];
      if (this.currentObject) {
        this.payload = JSON.parse(JSON.stringify(this.currentObject));
        if (this.hasAssignedObjects) {
          await this.getAssignedObjects(this.currentObjectId);
          this.objects = JSON.parse(JSON.stringify(this.currentAssignedObjects));
        }
      } else {
        if (this.customParentRoute) {
          this.customParentRoute();
        } else {
          this.$router.push({
            name: this.moduleName,
          });
        }
      }

      this.isEditing = false;
      this.isStickyButtonsValid = true;
      this.isStickyButtonsVisible = this.isModal;

      if (this.isNewCard) {
        this.isNewCard = false;
        if (this.hasGeometry && this.currentObject?.geometry) {
          const { crs } = this.currentObject.geometry;
          this.$root.$emit('createGeomAndFitView', { ...this.currentObject.geometry, crs });
        }
      }
    },
    async objectAdding() {
      try {
        this.isStickyButtonsLoading = true;
        const id = await this.handleObjectAdding(this.payload, this.objects);
        if (!this.isModal) {
          await this.$router.push(`${id}`);
        }
        this.refreshMapAndTable();
        this.isStickyButtonsLoading = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.addedCustomModule', { module: this.$i18n.t(`modules.${this.moduleName}`) }),
        });
      } catch (error) {
        console.log('~ error', error);
        this.isStickyButtonsLoading = false;
      }
    },
    async objectEditing() {
      try {
        this.isStickyButtonsLoading = true;
        if (this.hasAssignedObjects) {
          const objects = { add: this.getAddedObjects(), delete: this.getDeletedObjects() };
          await this.handleObjectEditing(this.payload, objects);
        } else {
          await this.handleObjectEditing(this.payload);
        }
        this.isStickyButtonsLoading = false;
        this.refreshMapAndTable();
        this.$root.$emit('getObjectHistory', {
          data_source_name: this.moduleDataSourceName,
          feature_id: this.currentObjectId,
        });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.editedCustomModule', { module: this.$i18n.t(`modules.${this.moduleName}`) }),
        });
        this.initEdit();
      } catch (error) {
        console.log('~ error', error);
        this.isStickyButtonsLoading = false;
      }
    },
    async prepareFormSchema() {
      const schema = this.getFormSchema();
      const schemaForm = this.getForm(schema.elements, this.attributesMetadata.attributes_schema.attributes, {
        filteredOutAttributes: this.filteredAttributes,
      });
      return schemaForm;
    },
    async objectDeleting() {
      try {
        this.isStickyButtonsLoading = true;
        this.isDeleteLoading = true;
        await this.handleObjectDeleting(this.currentObjectId);
        this.refreshMapAndTable();
        this.isDeleteObjectDialogVisible = false;
        if (this.isModal) {
          //
        } else if (this.customParentRoute) {
          this.customParentRoute();
        } else {
          await this.$router.push({
            name: this.parentModuleName ? this.parentModuleName : this.moduleName,
          });
        }
        this.isStickyButtonsLoading = false;
        this.isDeleteLoading = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.deletedCustomModule', { module: this.$i18n.t(`modules.${this.moduleName}`) }),
        });
      } catch (error) {
        console.log('~ error', error);
        this.isDeleteObjectDialogVisible = false;
        this.isStickyButtonsLoading = false;
        this.isDeleteLoading = false;
      }
    },
  },
  watch: {
    currentObjectId(nV) {
      if (nV || nV == '0') {
        this.loaded = false;
        this.init();
      }
    },
    isNew: {
      immediate: true,
      handler(nV) {
        if (nV === true) {
          this.$root.$emit('moduleIdentification-action', { isActive: false, moduleRoute: '' });
        } else {
          this.$root.$emit('moduleIdentification-action', { isActive: true, moduleRoute: this.$route.name });
        }
      },
    },
    computedIsValid: {
      immediate: true,
      handler(nV) {
        this.isStickyButtonsValid = nV;
      },
    },
    mapPanelDataFetched: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.init();
        }
      },
    },
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>
